import type { AxiosFieldValidationErrorResponse } from '../types/axios-errors';
import type { FieldValidationError } from '../types/generated';

export type { CustomAxiosRequestConfig, NotificationConfig } from '../notifications';

// `ApiErrorResponse` is owned by the typeshared `api/types/generated.ts`. It
// declares `action?: string`, but the wire contract is nullable — treat
// `ParsedApiError.action` below as the accurate shape.

/**
 * Parsed error result
 */
export interface ParsedApiError {
  title: string;
  message: string;
  error?: string;
  action?: string | null;
}

/**
 * Parse API error from various formats:
 * - Plain string error message
 * - JSON with {error, action, message} format
 * - Axios error with response.data
 * - Generic Error object
 */
export function parseApiError(error: any): ParsedApiError {
  // Handle null/undefined
  if (!error) {
    return {
      title: 'Unknown Error',
      message: 'An unknown error occurred. Please try again.',
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      title: 'Error',
      message: error,
    };
  }

  // Handle Axios errors with response.data
  if (error.response?.data) {
    if (error.response.data.error === 'validation_error') {
      return parseApiFieldValidationError(error);
    }
    return parseApiErrorData(error.response.data);
  }

  // Handle direct API error response objects
  if (error.error && error.message) {
    return parseApiErrorData(error);
  }

  // Handle standard Error objects
  if (error.message) {
    return {
      title: 'Error',
      message: error.message,
    };
  }

  // Fallback for any other format
  return {
    title: 'Error',
    message: 'An unexpected error occurred. Please try again.',
  };
}

/**
 * Parse the API error response data object
 */
function parseApiErrorData(data: any): ParsedApiError {
  if (typeof data === 'string') {
    return {
      title: 'Error',
      message: data,
    };
  }

  if (data.error && data.message) {
    // New JSON format: {error, action, message}
    return {
      title: getErrorTitle(data.error),
      message: data.message,
      error: data.error,
      action: data.action,
    };
  }

  // Try to extract any message-like property
  const message = data.message || data.error || data.detail || 'An error occurred';

  return {
    title: 'Error',
    message: typeof message === 'string' ? message : 'An error occurred',
  };
}

function parseApiFieldValidationError(error: AxiosFieldValidationErrorResponse): ParsedApiError {
  const responseData = error.response?.data;

  if (responseData.error !== 'validation_error') {
    return {
      title: 'Validation Error',
      message: 'There was a validation error with your request.',
    };
  }

  // Handle case where errors array is not present (simple validation error with message)
  if (!responseData.errors || !Array.isArray(responseData.errors)) {
    return {
      title: 'Validation Error',
      message: responseData.message || 'There was a validation error with your request.',
    };
  }

  const messages = responseData.errors
    .map((fieldError: FieldValidationError) => {
      return `${fieldError.field}: ${fieldError.message}`;
    })
    .join(', ');

  return {
    title: responseData.message,
    message: messages,
  };
}

/**
 * Convert error code to user-friendly title
 */
function getErrorTitle(errorCode: string): string {
  const errorTitles: Record<string, string> = {
    validation_error: 'Validation Error',
    authentication_error: 'Authentication Error',
    authorization_error: 'Permission Denied',
    subscription_error: 'Subscription Error',
    payment_error: 'Payment Error',
    rate_limit_error: 'Rate Limit Exceeded',
    server_error: 'Server Error',
    not_found: 'Not Found',
    conflict: 'Conflict',
    bad_request: 'Bad Request',
    expired_token: 'Session Expired',
    invalid_token: 'Invalid Token',
    invalid_session: 'Invalid Session',
    oauth_error: 'Authentication Failed',
    invalid_redirect_uri: 'Invalid Redirect',
    session_not_found: 'Session Not Found',
    email_not_found: 'Email Not Found',
    email_already_in_use: 'Email In Use',
    email_error: 'Email Error',
    invalid_email: 'Invalid Email',
    invalid_input: 'Invalid Input',
    database_error: 'Server Error',
    cache_error: 'Server Error',
    // Session / auth errors
    invalid_auth_method_nostr: 'Wrong Login Method',
    invalid_auth_method: 'Wrong Login Method',
    invalid_challenge: 'Invalid Login Link',
    challenge_mismatch: 'Invalid Login Link',
    invalid_nostr_signature: 'Invalid Signature',
    invalid_nostr_challenge: 'Invalid Challenge',
    nostr_key_mismatch: 'Key Mismatch',
    email_not_primary: 'Not Primary Email',
    too_many_attempts: 'Too Many Attempts',
    invalid_authorization_code: 'Invalid Code',
    expired_or_used_auth_code: 'Code Expired',
    invalid_2fa_code: 'Invalid Code',
    // 2FA errors
    '2fa_not_enabled': '2FA Not Enabled',
    '2fa_already_enabled': '2FA Already Enabled',
    invalid_code: 'Invalid Code',
    code_expired: 'Code Expired',
    no_recovery_codes: 'No Recovery Codes',
    invalid_method_type: 'Invalid Method',
    forbidden: 'Permission Denied',
    // Token errors
    token_not_valid_yet: 'Token Not Valid Yet',
    // Session errors
    invalid_challenge_type: 'Invalid Challenge',
    invalid_nostr_event: 'Invalid Authentication',
    invalid_request: 'Invalid Request',
    // Email delivery errors
    email_already_verified: 'Already Verified',
    email_not_verified: 'Email Not Verified',
    cannot_delete_account_email: 'Cannot Delete Email',
    inactive_recipient: 'Email Undeliverable',
    invalid_address: 'Invalid Address',
    spam_complaint: 'Email Rejected',
    hard_bounce: 'Email Undeliverable',
    service_unavailable: 'Service Unavailable',
    rate_limit_exceeded: 'Rate Limit Exceeded',
    temporary_failure: 'Temporary Error',
    // Payment errors
    payment_exists: 'Payment Exists',
    checkout_session_failed: 'Checkout Failed',
    invalid_currency: 'Invalid Currency',
    payment_gateway_not_found: 'Gateway Not Found',
    invalid_payment_gateway_owner: 'Permission Denied',
    order_not_found: 'Order Not Found',
    payment_not_found: 'Payment Not Found',
    customer_already_exists: 'Customer Exists',
    webhook_error: 'Processing Error',
    insufficient_funds: 'Insufficient Funds',
    // Authorization
    insufficient_scope: 'Permission Denied',
    // Generic
    internal_error: 'Server Error',
  };

  return errorTitles[errorCode] || 'Error';
}

/**
 * Show error notification matching application style
 */
export function showApiErrorNotification(error: any, notifications: any, defaultTitle = 'Error') {
  const parsed = parseApiError(error);

  // Enhance message with action hint if available
  let actionSuffix = '';
  if (parsed.action) {
    switch (parsed.action) {
      case 'retry_later':
        actionSuffix = ' Please try again later.';
        break;
      case 'add_credits':
        actionSuffix = ' Please add credits to your account.';
        break;
      case 'contact_support':
        actionSuffix = ' Please contact support for assistance.';
        break;
      case 'cancel_payments_first':
        actionSuffix = ' Cancel associated payments first.';
        break;
      case 'request_new_code':
        actionSuffix = ' Please request a new code.';
        break;
    }
  }

  const message = parsed.message;
  const finalMessage =
    actionSuffix && !message.toLowerCase().includes(actionSuffix.trim().toLowerCase().slice(0, 20))
      ? `${message}${actionSuffix}`
      : message;

  notifications.show({
    title: parsed.title || defaultTitle,
    message: finalMessage,
    color: 'red',
    autoClose: 10000,
  });
}

/**
 * Show success notification matching application style
 */
export function showSuccessNotification(title: string, message: string, notifications: any) {
  notifications.show({
    title,
    message,
    color: 'green',
    autoClose: 5000,
  });
}
